import React from 'react';
import { ArrowLeft, FileUp, Pencil, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import { Button } from '@/components/ui/button';

interface CreationModeChoiceProps {
  onSelectManual: () => void;
  onSelectAi: () => void;
  onSelectImport: () => void;
  onCancel: () => void;
}

/**
 * The three routes into a new product, side by side. Extracted because the
 * only thing that differs between them is the glyph, the copy and the handler.
 */
const ModeCard: React.FC<{
  icon: React.ReactNode;
  title: React.ReactNode;
  description: string;
  onClick: () => void;
  delay: number;
}> = ({ icon, title, description, onClick, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay, ease: [0.25, 0.1, 0.25, 1] as const }}
    style={{ flex: 1, display: 'flex' }}
  >
    <button
      type="button"
      onClick={onClick}
      className="flex flex-1 flex-col items-center rounded-xl border bg-card p-8 text-center transition-colors hover:border-foreground/30 hover:bg-accent/40"
    >
      <span className="mb-5 flex size-14 items-center justify-center bg-muted text-muted-foreground [&_svg]:size-[26px]">
        {icon}
      </span>
      <span className="mb-1.5 flex items-center gap-2 font-heading text-base font-medium tracking-tight text-foreground">
        {title}
      </span>
      <span className="text-sm leading-relaxed text-muted-foreground">{description}</span>
    </button>
  </motion.div>
);

export const CreationModeChoice: React.FC<CreationModeChoiceProps> = ({
  onSelectManual,
  onSelectAi,
  onSelectImport,
  onCancel,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-[calc(100vh-140px)] flex-col">
      {/* Back button row */}
      <div className="flex justify-start px-4 pt-2 sm:px-6">
        <Button
          variant="ghost"
          onClick={onCancel}
          className="h-9 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="rtl:rotate-180" />
          {t('COMMON.BACK')}
        </Button>
      </div>

      {/* Centered content */}
      <div className="flex flex-1 flex-col items-center px-4 pt-20 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] as const }}
          style={{ width: '100%', maxWidth: 880 }}
        >
          {/* Header */}
          <div className="mb-10 text-center">
            <h1 className="mb-2 font-heading text-2xl font-medium tracking-tight text-foreground">
              {t('PAGES.NEW_PRODUCT.V3.CREATION_MODE.TITLE')}
            </h1>
            <p className="mx-auto max-w-[400px] text-sm leading-relaxed text-muted-foreground">
              {t('PAGES.NEW_PRODUCT.V3.CREATION_MODE.SUBTITLE')}
            </p>
          </div>

          {/* Cards */}
          <div className="flex gap-5">
            <ModeCard
              icon={<Pencil strokeWidth={1.5} />}
              title={t('PAGES.NEW_PRODUCT.V3.CREATION_MODE.MANUAL_TITLE')}
              description={t('PAGES.NEW_PRODUCT.V3.CREATION_MODE.MANUAL_DESC')}
              onClick={onSelectManual}
              delay={0.1}
            />

            <ModeCard
              icon={<FileUp strokeWidth={1.5} />}
              title={t('PAGES.NEW_PRODUCT.V3.CREATION_MODE.IMPORT_TITLE')}
              description={t('PAGES.NEW_PRODUCT.V3.CREATION_MODE.IMPORT_DESC')}
              onClick={onSelectImport}
              delay={0.18}
            />

            <ModeCard
              icon={<Sparkles strokeWidth={1.5} />}
              title={
                <>
                  {t('PAGES.NEW_PRODUCT.V3.CREATION_MODE.AI_TITLE')}
                  <span className="border px-1 py-px text-[9px] leading-snug font-medium tracking-wider text-muted-foreground uppercase">
                    Beta
                  </span>
                </>
              }
              description={t('PAGES.NEW_PRODUCT.V3.CREATION_MODE.AI_DESC')}
              onClick={onSelectAi}
              delay={0.26}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};
